import { useCallback, useRef, useSyncExternalStore } from 'react';
import type { IEventField } from './AssistanceViewContext';

/*
 * Subscribable store for the transient interaction state of the assistance view (which field
 * is focused / hovered / updating / reselecting). Kept outside of React state so that a
 * change only re-renders the components that subscribed to the affected slice — hovering or
 * focusing a field must not re-render the whole form tree.
 *
 * The store is written exclusively by AssistanceViewContextProvider (which intercepts all
 * field events) and reset when the provider unmounts. It is a module singleton: only one
 * assistance view is mounted at a time.
 */

export interface AssistanceInteractionState {
    focusField: IEventField | null;
    hoverField: IEventField | null;
    updateField: IEventField | null;
    reselectField: IEventField | null;
    hoverItemIndex: number | null;
}

const initialState: AssistanceInteractionState = {
    focusField: null,
    hoverField: null,
    updateField: null,
    reselectField: null,
    hoverItemIndex: null,
};

let state: AssistanceInteractionState = initialState;
const listeners = new Set<() => void>();

const setState = (partial: Partial<AssistanceInteractionState>) => {
    state = { ...state, ...partial };
    listeners.forEach((listener) => listener());
};

export const interactionStore = {
    getState: () => state,
    setState,
    reset: () => setState(initialState),
    subscribe: (listener: () => void) => {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
};

const shallowEqual = (a: unknown, b: unknown): boolean => {
    if (Object.is(a, b)) return true;
    if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;
    const keysA = Object.keys(a);
    if (keysA.length !== Object.keys(b).length) return false;
    return keysA.every((key) => Object.is((a as any)[key], (b as any)[key]));
};

export const useInteractionSelector = <T>(selector: (state: AssistanceInteractionState) => T): T => {
    const selectorRef = useRef(selector);
    selectorRef.current = selector;
    // cache the selected value so getSnapshot returns a stable reference for
    // shallow-equal results — useSyncExternalStore compares snapshots with Object.is
    const cacheRef = useRef<{ value: T } | null>(null);

    const getSnapshot = useCallback(() => {
        const next = selectorRef.current(state);
        if (cacheRef.current && shallowEqual(cacheRef.current.value, next)) {
            return cacheRef.current.value;
        }
        cacheRef.current = { value: next };
        return next;
    }, []);

    return useSyncExternalStore(interactionStore.subscribe, getSnapshot);
};

// same matching the sections used to compute per-field activity: an event field matches by
// plain or full field name, and by item index (undefined for header fields)
const matchesField = (
    eventField: IEventField | null,
    fieldName: string,
    fullFieldName: string,
    itemIndex: number | undefined
) =>
    !!eventField &&
    (eventField.fieldName === fieldName || eventField.fieldName === fullFieldName) &&
    eventField.itemIndex === itemIndex;

export interface FieldActivity {
    isFocusActive: boolean;
    isUpdateActive: boolean;
    isReselectActive: boolean;
}

export const useFieldActivity = (fieldName: string, fullFieldName: string, itemIndex?: number): FieldActivity =>
    useInteractionSelector((s) => ({
        isFocusActive: matchesField(s.focusField, fieldName, fullFieldName, itemIndex),
        isUpdateActive: matchesField(s.updateField, fieldName, fullFieldName, itemIndex),
        isReselectActive: matchesField(s.reselectField, fieldName, fullFieldName, itemIndex),
    }));
